import { CheckCircle2, Cloud, ImagePlus, LockKeyhole, UploadCloud } from "lucide-react";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { TurnstileWidget } from "../components/TurnstileWidget";
import { defaultApprovalNotice, defaultConfirmationMessage, defaultGuestUploadInstructions, getGuestUploadLink } from "../lib/api";
import { formatDate } from "../lib/format";
import { publicEventStyle, safeAssetUrl } from "../lib/personalization";
import type { EventPersonalization } from "../lib/types";
import { useConfig } from "../lib/useConfig";
import { useEventPersonalization } from "../lib/useEventPersonalization";

export function UploadPage() {
  const params = useParams();
  const config = useConfig({ optional: Boolean(params.uploadSlug) });
  const [files, setFiles] = useState<File[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [linkEventDetails, setLinkEventDetails] = useState<EventPersonalization | null>(null);
  const [resolvedLink, setResolvedLink] = useState<{ clientSlug: string; eventSlug: string; instructions?: string } | null>(null);
  const [linkConfig, setLinkConfig] = useState<{
    turnstileSiteKey?: string;
    maxFilesPerUpload?: number;
  } | null>(null);

  const uploadSlug = params.uploadSlug;
  const clientSlug = resolvedLink?.clientSlug || params.clientSlug || config?.clientSlug;
  const eventSlug = resolvedLink?.eventSlug || params.eventSlug || config?.eventSlug;
  const turnstileSiteKey = linkConfig?.turnstileSiteKey || config?.turnstileSiteKey || "";
  const maxFiles = linkConfig?.maxFilesPerUpload || config?.maxFilesPerUpload || 20;
  const routeEventDetails = useEventPersonalization(clientSlug, eventSlug);
  const eventDetails = linkEventDetails || routeEventDetails;
  const heroImage = safeAssetUrl(eventDetails?.optionalHeroImage);
  const logo = safeAssetUrl(eventDetails?.optionalLogo);
  const vaultDisplayName = eventDetails?.vaultDisplayName || eventDetails?.uploadPageTitle || "Memory Media Vault";
  const uploadInstructions =
    eventDetails?.guestUploadInstructions || eventDetails?.uploadPageIntroText || defaultGuestUploadInstructions;
  const approvalNotice = eventDetails?.approvalNotice || defaultApprovalNotice;
  const confirmationMessage = eventDetails?.confirmationMessage || defaultConfirmationMessage;

  useEffect(() => {
    if (!uploadSlug) return;
    let active = true;
    setError("");
    void getGuestUploadLink(uploadSlug)
      .then((result) => {
        if (!active) return;
        setResolvedLink({
          clientSlug: result.link.clientSlug,
          eventSlug: result.link.eventSlug,
          instructions: result.link.instructions,
        });
        setLinkEventDetails(result.event);
        setLinkConfig(result.config || null);
      })
      .catch((linkError) => {
        if (active) {
          setError(linkError instanceof Error ? linkError.message : "This guest upload link could not be loaded.");
        }
      });
    return () => {
      active = false;
    };
  }, [uploadSlug]);

  const fileSummary = useMemo(() => {
    if (!files.length) return "No files selected yet";
    return `${files.length} file${files.length === 1 ? "" : "s"} selected`;
  }, [files.length]);

  if (!clientSlug || !eventSlug) {
    if (uploadSlug && error) {
      return (
        <main className="px-4 py-16 sm:px-6 lg:px-8">
          <section className="vault-rail ivory-card mx-auto max-w-3xl rounded-lg p-8 text-center text-ktd-rich sm:p-12">
            <h1 className="font-display text-4xl font-bold">Upload link unavailable</h1>
            <p className="mt-4 leading-7 text-ktd-muted">{error}</p>
          </section>
        </main>
      );
    }
    return (
      <main className="px-4 py-16 sm:px-6 lg:px-8">
        <section className="vault-rail ivory-card mx-auto max-w-3xl rounded-lg p-8 text-center text-ktd-rich sm:p-12">
          <h1 className="font-display text-4xl font-bold">Opening upload link...</h1>
          <p className="mt-4 leading-7 text-ktd-muted">Loading the event vault connected to this guest upload link.</p>
        </section>
      </main>
    );
  }

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    setBusy(true);
    const form = event.currentTarget;
    const formData = new FormData(form);
    formData.set("clientSlug", clientSlug);
    formData.set("eventSlug", eventSlug);
    if (uploadSlug) formData.set("uploadSlug", uploadSlug);
    files.forEach((file) => formData.append("files", file));

    try {
      const response = await fetch("/api/upload", {
        method: "POST",
        body: formData,
      });
      if (!response.ok) throw new Error(await response.text());
      setSubmitted(true);
      form.reset();
      setFiles([]);
    } catch (uploadError) {
      setError(
        uploadError instanceof Error
          ? uploadError.message
          : "We could not submit your uploads. Please try again or contact the event host.",
      );
    } finally {
      setBusy(false);
    }
  };

  if (submitted) {
    return (
      <main className="px-4 py-16 sm:px-6 lg:px-8" style={publicEventStyle(eventDetails)}>
        <section className="vault-rail ivory-card mx-auto max-w-3xl rounded-lg p-8 text-center text-ktd-rich sm:p-12">
          <CheckCircle2 className="mx-auto size-14 text-[#8a6c14]" />
          <h1 className="mt-6 font-display text-4xl font-bold">Thank you for sharing your memories.</h1>
          <p className="mt-4 leading-7 text-ktd-muted">{confirmationMessage}</p>
          <Link
            to={`/gallery/${clientSlug}/${eventSlug}`}
            className="focus-ring event-button mt-8 inline-flex rounded-lg px-6 py-3 text-sm font-extrabold"
          >
            {eventDetails?.galleryButtonLabel || "View Vault Gallery"}
          </Link>
        </section>
      </main>
    );
  }

  return (
    <main className="px-4 py-12 sm:px-6 lg:px-8 lg:py-16" style={publicEventStyle(eventDetails)}>
      <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[.85fr_1.15fr]">
        <aside className="event-masthead rounded-lg p-7 text-white shadow-luxe sm:p-9">
          {logo && <img src={logo} alt={`${eventDetails?.clientName || "Client"} logo`} className="mb-6 max-h-14 max-w-44 object-contain" />}
          <p className="text-xs font-extrabold uppercase tracking-[0.24em] text-ktd-softGold">
            Guest / Family Vault Upload
          </p>
          <h1 className="mt-4 text-balance font-display text-4xl font-bold leading-tight sm:text-5xl">
            {vaultDisplayName}
          </h1>
          <div className="mt-5 flex flex-wrap gap-2 text-xs font-extrabold uppercase tracking-[0.16em] text-white/62">
            <span>{eventDetails?.clientName || "Kreatethedate Client"}</span>
            <span>{eventDetails?.eventType || "Event"}</span>
            {eventDetails?.eventDate && <span>{formatDate(eventDetails.eventDate)}</span>}
          </div>
          <p className="mt-5 text-lg leading-8 text-white/72">
            {resolvedLink?.instructions || uploadInstructions}
          </p>
          <div className="mt-8 grid gap-4">
            {[
              { icon: LockKeyhole, text: approvalNotice },
              { icon: Cloud, text: "Photos and videos are stored in the private client-owned vault." },
              { icon: ImagePlus, text: "Add your name and an optional message so the client knows who shared each memory." },
              { icon: ImagePlus, text: "Approved memories may appear in the Memory Media Vault or approved gallery." },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.text} className="vault-rail flex gap-4 rounded-lg border border-ktd-gold/18 bg-white/[0.05] p-4">
                  <Icon className="size-5 shrink-0 text-ktd-softGold" />
                  <p className="text-sm leading-6 text-white/66">{item.text}</p>
                </div>
              );
            })}
          </div>
          {heroImage && (
            <img
              src={heroImage}
              alt={eventDetails?.eventName || "Event"}
              className="mt-8 aspect-[16/10] w-full rounded-lg border border-white/15 object-cover"
            />
          )}
        </aside>

        <form onSubmit={submit} className="upload-form vault-rail ivory-card min-w-0 rounded-lg p-5 text-ktd-rich sm:p-8">
          <div className="grid gap-5">
            <label className="grid gap-2 text-sm font-extrabold text-ktd-rich">
              Guest name
              <input
                name="guestName"
                required
                maxLength={120}
                className="form-field focus-ring w-full rounded-lg px-4 py-3 font-semibold"
                placeholder="Your name"
              />
            </label>
            <label className="grid gap-2 text-sm font-extrabold text-ktd-rich">
              Email or phone
              <input
                name="contact"
                maxLength={160}
                className="form-field focus-ring w-full rounded-lg px-4 py-3 font-semibold"
                placeholder="Optional"
              />
            </label>
            <label className="grid gap-2 text-sm font-extrabold text-ktd-rich">
              Relationship to client/event
              <input
                name="relationship"
                maxLength={120}
                className="form-field focus-ring w-full rounded-lg px-4 py-3 font-semibold"
                placeholder="Friend, cousin, coworker, guest"
              />
            </label>
            <label className="grid gap-2 text-sm font-extrabold text-ktd-rich">
              Optional message or caption
              <textarea
                name="caption"
                maxLength={800}
                rows={4}
                className="form-field focus-ring w-full rounded-lg px-4 py-3 font-semibold"
                placeholder="Add a message, memory, note, or caption"
              />
            </label>
            <label className="grid gap-3 rounded-lg border border-dashed border-ktd-gold/45 bg-white/95 p-4 text-sm font-extrabold text-ktd-rich sm:p-5">
              <span className="flex items-center gap-3">
                <UploadCloud className="size-6 text-[#8a6c14]" />
                File upload
              </span>
              <input
                name="filesInput"
                type="file"
                required
                multiple
                accept=".jpg,.jpeg,.png,.webp,.mp4,.mov,.webm,image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm"
                onChange={(event) => {
                  const selected = Array.from(event.target.files || []).slice(0, maxFiles);
                  setFiles(selected);
                }}
                className="upload-file-input focus-ring w-full rounded-lg bg-white text-sm font-semibold"
              />
              <span className="text-xs font-semibold leading-5 text-[#4d4234]">
                {fileSummary}. Accepted: jpg, jpeg, png, webp, mp4, mov, webm. Max files per upload: {maxFiles}.
              </span>
            </label>
            <label className="flex items-start gap-3 rounded-lg border border-ktd-gold/18 bg-white p-4 text-sm font-semibold leading-6 text-[#3c3328]">
              <input name="consent" type="checkbox" required className="mt-1 size-4 accent-ktd-gold" />
              <span>
                I understand that my submitted photos and videos may be reviewed before appearing in the Memory Media
                Vault. Admin approval is required before media goes live.
              </span>
            </label>
            {turnstileSiteKey ? (
              <TurnstileWidget siteKey={turnstileSiteKey} />
            ) : (
              <p className="rounded-lg border border-ktd-gold/25 bg-white p-4 text-sm font-semibold leading-6 text-[#4d4234]">
                Upload protection will appear here after the Cloudflare Turnstile site key is configured.
              </p>
            )}
            {error && <p className="rounded-lg bg-red-50 p-4 text-sm font-semibold leading-6 text-red-800">{error}</p>}
            <button
              type="submit"
              disabled={busy}
              className="focus-ring event-button min-h-12 rounded-lg px-6 py-3 text-sm font-extrabold shadow-gold transition disabled:opacity-60"
            >
              {busy ? "Submitting Uploads" : eventDetails?.uploadButtonLabel || "Upload Memories"}
            </button>
          </div>
        </form>
      </div>
    </main>
  );
}
